/**
 * Convert a YouTube watch/share/live URL into its privacy-friendly embed URL,
 * or return null for any non-YouTube (or unparseable) URL. No regex — parsed
 * with the URL API and literal path segments.
 */

/** YouTube hosts we recognize, mapped to how their video id is located. */
const SHORT_HOST = 'youtu.be';
const LONG_HOSTS: ReadonlySet<string> = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com']);

/** Path segments that precede the video id on the long host (e.g. /live/<id>). */
const ID_PREFIXES: ReadonlySet<string> = new Set(['live', 'embed', 'v', 'shorts']);

/** Build the nocookie embed URL for a known video id. */
function embedUrl(videoId: string): string {
  return `https://www.youtube-nocookie.com/embed/${videoId}`;
}

/** Extract a video id from a long-host (youtube.com) URL. */
function idFromLongHost(parsed: URL): string | null {
  if (parsed.pathname === '/watch') return parsed.searchParams.get('v');
  const [, prefix, id] = parsed.pathname.split('/');
  return ID_PREFIXES.has(prefix) && id ? id : null;
}

/** Extract a video id from any supported YouTube URL, or null. */
function videoId(parsed: URL): string | null {
  if (parsed.hostname === SHORT_HOST) return parsed.pathname.slice(1) || null;
  if (LONG_HOSTS.has(parsed.hostname)) return idFromLongHost(parsed);
  return null;
}

/** YouTube embed URL for a watch/live/share link, or null if not YouTube. */
export function youtubeEmbedUrl(url: string): string | null {
  if (!url) return null;
  try {
    const id = videoId(new URL(url));
    return id ? embedUrl(id) : null;
  } catch {
    return null;
  }
}
